import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToasterService } from 'src/app/services/core/toaster.service';
import { HrDataService } from '../../../hr/hr.service';
import { first, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';
import { Subject, Subscription } from 'rxjs';
import { DocumentPreviewService } from 'src/app/services/document-preview.service';

/**
 * PTO Request interface for user's own queue
 */
export interface PTOUserRequest {
  id: number;
  staff_id: number;
  staff_name: string;
  staff_department: string;
  staff_avatar?: string;
  type: 'Vacation' | 'Sick' | 'Personal';
  start_date: string;
  end_date: string;
  days: number;
  status: 'pending' | 'approved' | 'denied' | 'Cancel';
  notes?: string;
  submitted_date: string;
  selected?: boolean;
  isCancelling?: boolean;
  documents?: PTORequestDocument[];
}

/**
 * A file already uploaded via POST /api/v1/attachments and linked to the
 * request (see AttachmentResource on the backend).
 */
export interface PTORequestDocument {
  id: number;
  file_type: string;
  attachment: string;
  original_name: string;
  thumbnail?: string;
}

/**
 * Grouped requests by staff member
 */
export interface GroupedRequests {
  staff_id: number;
  staff_name: string;
  staff_department: string;
  staff_avatar?: string;
  requests: PTOUserRequest[];
  expanded: boolean;
  allSelected: boolean;
  pendingCount: number;
}

/**
 * Filter options for the user queue
 */
export interface UserQueueFilters {
  searchTerm: string;
  type: string;
  status: string;
}

export interface PTOApprovalQueueDialogData {
  staffId: number;
  staffName: string;
}

@Component({
    selector: 'app-pto-approval-queue-dialog',
    templateUrl: './pto-approval-queue-dialog.component.html',
    styleUrls: ['./pto-approval-queue-dialog.component.scss'],
    standalone: false
})
export class PTOApprovalQueueDialogComponent implements OnInit, OnDestroy {
  // All PTO requests
  public allRequests: PTOUserRequest[] = [];
  public filteredRequests: PTOUserRequest[] = [];
  public groupedRequests: GroupedRequests[] = [];

  // Selected request for detail panel
  public selectedRequest: PTOUserRequest | null = null;

  // Filters
  public filters: UserQueueFilters = {
    searchTerm: '',
    type: 'all',
    status: 'all',
  };

  // Filter options
  public typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'Vacation', label: 'Vacation' },
    { value: 'Sick', label: 'Sick Leave' },
    { value: 'Personal', label: 'Personal' },
  ];

  public statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'denied', label: 'Denied' },
    { value: 'Cancel', label: 'Cancel' },
  ];

  // Stats
  public totalRequests: number = 0;
  public pendingCount: number = 0;
  public approvedCount: number = 0;
  public deniedCount: number = 0;

  // Bulk selection
  public selectAll: boolean = false;
  public selectedCount: number = 0;

  // Loading state
  public isLoading: boolean = false;
  public isProcessing: boolean = false;

  // Search functionality
  private searchInput = new Subject<string>();
  private subscription = new Subscription();
  private debounceTimeMs: number = 500;

  // Pagination
  public currentPage: number = 1;
  public perPage: number = 20;
  public pagination: any = null;
  public isLoadingMore: boolean = false;

  // API Summary data
  public apiSummary: any = null;

  constructor(
    public dialogRef: MatDialogRef<PTOApprovalQueueDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PTOApprovalQueueDialogData,
    private toasterService: ToasterService,
    private hrDataService: HrDataService,
    private documentPreviewService: DocumentPreviewService
  ) {}

  ngOnInit(): void {
    this.initializeSearch();
    this.loadPTORequests(false);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Initialize search with debounce
   */
  initializeSearch(): void {
    const searchSubscription = this.searchInput
      .pipe(
        debounceTime(this.debounceTimeMs),
        distinctUntilChanged(),
        switchMap(async () => {
          this.currentPage = 1;
          this.loadPTORequests(false);
        })
      )
      .subscribe();
    this.subscription.add(searchSubscription);
  }

  /**
   * Handle search text change
   */
  onSearchTextChange(): void {
    this.searchInput.next(this.filters.searchTerm);
  }

  /**
   * Load PTO requests from API
   */
  loadPTORequests(loadMore: boolean = false): void {
    if (!loadMore) {
      this.isLoading = true;
      this.currentPage = 1;
      this.allRequests = [];
      this.groupedRequests = [];
    } else {
      this.isLoadingMore = true;
    }

    // Build query params - INCLUDE staff_id for user's own requests
    const params: any = {
      staff_id: this.data.staffId,
      page: this.currentPage,
      per_page: this.perPage,
    };

    // Add filters
    if (this.filters.searchTerm) {
      params.search = this.filters.searchTerm;
    }
    if (this.filters.status !== 'all') {
      params.status = this.filters.status;
    }
    if (this.filters.type !== 'all') {
      params.time_off_type = this.filters.type.toLowerCase();
    }

    // Call API
    this.hrDataService
      .getPTOApprovalQueue(params)
      .pipe(first())
      .subscribe({
        next: (response: any) => {
          this.mapAPIResponse(response, loadMore);
          this.isLoading = false;
          this.isLoadingMore = false;
        },
        error: (error: any) => {
          console.error('Failed to fetch PTO requests:', error);
          this.isLoading = false;
          this.isLoadingMore = false;
          this.allRequests = [];
          this.groupedRequests = [];
          this.toasterService.showError(
            'Error',
            'Failed to load your requests'
          );
        },
      });
  }

  /**
   * Map API response to component data structures
   */
  private mapAPIResponse(response: any, loadMore: boolean): void {
    const apiData = response?.data?.data;

    if (!apiData || !Array.isArray(apiData)) {
      this.allRequests = [];
      this.groupedRequests = [];
      this.pagination = null;
      this.apiSummary = null;
      return;
    }

    // Store pagination and summary
    this.pagination = response?.data?.pagination;
    this.apiSummary = response?.data?.summary;

    // Update stats from API summary
    if (this.apiSummary) {
      this.totalRequests = this.apiSummary.total_requests || 0;
      this.pendingCount = this.apiSummary.pending || 0;
      this.approvedCount = this.apiSummary.approved || 0;
      this.deniedCount = this.apiSummary.denied || 0;
    }

    // Map API data to PTOUserRequest format
    const mappedRequests: PTOUserRequest[] = [];

    apiData.forEach((staffGroup: any) => {
      const staffRequests = staffGroup.requests || [];

      staffRequests.forEach((request: any) => {
        // Convert time_off_type to proper case
        let type: 'Vacation' | 'Sick' | 'Personal' = 'Vacation';
        const timeOffType = (request.time_off_type || '').toLowerCase();

        if (timeOffType.includes('sick')) {
          type = 'Sick';
        } else if (timeOffType.includes('personal')) {
          type = 'Personal';
        } else if (timeOffType.includes('vacation')) {
          type = 'Vacation';
        }

        // Format dates from ISO to YYYY-MM-DD
        const startDate = request.start_date
          ? request.start_date.split('T')[0]
          : '';
        const endDate = request.end_date ? request.end_date.split('T')[0] : '';

        // Normalize status - handle cancel variations (cancel, Cancelled, cancelled) to 'Cancel'
        let normalizedStatus: 'pending' | 'approved' | 'denied' | 'Cancel' =
          'pending';
        const apiStatus = (request.status || '').toLowerCase();

        if (apiStatus.includes('cancel')) {
          normalizedStatus = 'Cancel';
        } else if (apiStatus === 'approved') {
          normalizedStatus = 'approved';
        } else if (apiStatus === 'denied') {
          normalizedStatus = 'denied';
        } else {
          normalizedStatus = 'pending';
        }

        mappedRequests.push({
          id: request.id,
          staff_id: request.staff_id,
          staff_name: staffGroup.staff_name || this.data.staffName,
          staff_department:
            request.department?.name || staffGroup.department?.name || '',
          type: type,
          start_date: startDate,
          end_date: endDate,
          days: request.days_off_request || 0,
          status: normalizedStatus,
          notes: request.notes || undefined,
          submitted_date: startDate,
          selected: false,
          isCancelling: false,
          documents: request.documents || [],
        });
      });
    });

    // If loading more, append to existing requests
    if (loadMore) {
      this.allRequests = [...this.allRequests, ...mappedRequests];
    } else {
      this.allRequests = mappedRequests;
    }

    // Apply filters and grouping
    this.applyFilters();
  }

  /**
   * Apply filters and group requests
   */
  applyFilters(): void {
    // Since filtering is done on server-side, just use all requests
    this.filteredRequests = this.allRequests;

    // Group by staff
    this.groupRequests();
    this.updateSelectionCount();
  }

  /**
   * Trigger API call when filters change
   */
  onFiltersChange(): void {
    this.currentPage = 1;
    this.loadPTORequests(false);
  }

  /**
   * Load more requests (pagination)
   */
  loadMoreRequests(): void {
    if (this.hasMoreRequests()) {
      this.currentPage++;
      this.loadPTORequests(true);
    }
  }

  /**
   * Check if there are more requests to load
   */
  hasMoreRequests(): boolean {
    return this.pagination && this.currentPage < this.pagination.last_page;
  }

  /**
   * Group filtered requests by staff member
   */
  private groupRequests(): void {
    const groups = new Map<number, GroupedRequests>();

    this.filteredRequests.forEach((request) => {
      if (!groups.has(request.staff_id)) {
        groups.set(request.staff_id, {
          staff_id: request.staff_id,
          staff_name: request.staff_name,
          staff_department: request.staff_department,
          staff_avatar: request.staff_avatar,
          requests: [],
          expanded: true,
          allSelected: false,
          pendingCount: 0,
        });
      }

      const group = groups.get(request.staff_id)!;
      group.requests.push(request);
      if (request.status === 'pending') {
        group.pendingCount++;
      }
    });

    this.groupedRequests = Array.from(groups.values());

    // Update allSelected status for each group
    this.groupedRequests.forEach((group) => {
      const selectableRequests = group.requests.filter(
        (r) => r.status === 'pending'
      );
      group.allSelected =
        selectableRequests.length > 0 &&
        selectableRequests.every((r) => r.selected);
    });
  }

  /**
   * Update selection count
   */
  private updateSelectionCount(): void {
    this.selectedCount = this.filteredRequests.filter(
      (r) => r.selected && r.status === 'pending'
    ).length;
  }

  /**
   * Toggle group expansion
   */
  toggleGroup(group: GroupedRequests): void {
    group.expanded = !group.expanded;
  }

  /**
   * Select request for detail view
   */
  selectRequest(request: PTOUserRequest): void {
    this.selectedRequest = request;
  }

  /**
   * Toggle select all (pending requests only)
   */
  toggleSelectAll(): void {
    const pendingRequests = this.filteredRequests.filter(
      (r) => r.status === 'pending'
    );
    pendingRequests.forEach((r) => (r.selected = this.selectAll));
    this.groupRequests();
    this.updateSelectionCount();
  }

  /**
   * Toggle select all for a group
   */
  toggleGroupSelectAll(group: GroupedRequests): void {
    const pendingRequests = group.requests.filter(
      (r) => r.status === 'pending'
    );
    pendingRequests.forEach((r) => (r.selected = group.allSelected));
    this.updateSelectionCount();

    // Update global selectAll
    const allPending = this.filteredRequests.filter(
      (r) => r.status === 'pending'
    );
    this.selectAll =
      allPending.length > 0 && allPending.every((r) => r.selected);
  }

  /**
   * Toggle individual request selection
   */
  toggleRequestSelection(request: PTOUserRequest, event: Event): void {
    event.stopPropagation();
    request.selected = !request.selected;
    this.groupRequests();
    this.updateSelectionCount();

    // Update global selectAll
    const allPending = this.filteredRequests.filter(
      (r) => r.status === 'pending'
    );
    this.selectAll =
      allPending.length > 0 && allPending.every((r) => r.selected);
  }

  /**
   * Cancel a single request
   */
  cancelRequest(request: PTOUserRequest): void {
    if (request.status !== 'pending') {
      this.toasterService.showWarning(
        'Warning',
        'Only pending requests can be Cancel'
      );
      return;
    }

    request.isCancelling = true;

    this.hrDataService
      .updatePTORequestStatus(request.id, 'cancel')
      .pipe(first())
      .subscribe({
        next: (response: any) => {
          request.isCancelling = false;
          if (response?.data?.success) {
            this.toasterService.showSuccessMessage(
              response?.data?.message || 'Request Cancel successfully'
            );
            // Reload data
            this.loadPTORequests(false);
            // Clear selected request if it was Cancel
            if (this.selectedRequest?.id === request.id) {
              this.selectedRequest = null;
            }
          }
        },
        error: (error: any) => {
          request.isCancelling = false;
          console.error('Failed to cancel request:', error);
          this.toasterService.showError('Error', 'Failed to cancel request');
        },
      });
  }

  /**
   * Bulk cancel selected pending requests
   */
  bulkCancelRequests(): void {
    const selectedRequests = this.filteredRequests.filter(
      (req) => req.selected && req.status === 'pending'
    );

    if (selectedRequests.length === 0) {
      this.toasterService.showWarning(
        'Warning',
        'No pending requests selected'
      );
      return;
    }

    this.isProcessing = true;

    const time_off_ids = selectedRequests.map((req) => req.id);
    const payload = {
      time_off_ids: time_off_ids,
      status: 'Cancel',
    };

    this.hrDataService
      .bulkUpdateTimeOffStatus(payload)
      .pipe(first())
      .subscribe({
        next: (response: any) => {
          this.isProcessing = false;
          if (response?.data?.success) {
            this.toasterService.showSuccessMessage(
              response?.data?.message ||
                `${selectedRequests.length} request(s) Cancel successfully`
            );
            // Reload data
            this.loadPTORequests(false);
            // Clear selected request if it was Cancel
            if (
              this.selectedRequest &&
              selectedRequests.some(
                (req) => req.id === this.selectedRequest!.id
              )
            ) {
              this.selectedRequest = null;
            }
          }
        },
        error: (error: any) => {
          this.isProcessing = false;
          console.error('Failed to bulk cancel requests:', error);
          this.toasterService.showError('Error', 'Failed to cancel requests');
        },
      });
  }

  /**
   * Get type icon
   */
  getTypeIcon(type: string): string {
    const icons: { [key: string]: string } = {
      Vacation: 'wb_sunny',
      Sick: 'local_hospital',
      Personal: 'person',
    };
    return icons[type] || 'event';
  }

  /**
   * Get status icon
   */
  getStatusIcon(status: string): string {
    const icons: { [key: string]: string } = {
      pending: 'schedule',
      approved: 'check_circle',
      denied: 'cancel',
      Cancel: 'block',
    };
    return icons[status] || 'help';
  }

  /**
   * Icon for an attached document, by its file_type (from AttachmentResource)
   */
  getDocumentIcon(fileType: string): string {
    const ext = (fileType || '').toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return 'image';
    if (ext === 'pdf') return 'picture_as_pdf';
    if (['doc', 'docx'].includes(ext)) return 'description';
    if (['xls', 'xlsx'].includes(ext)) return 'table_chart';
    return 'insert_drive_file';
  }

  /**
   * Open an attached document in the app's in-app preview (the same eye-icon
   * dispatcher the Document Manager uses) instead of a new browser tab.
   */
  openDocumentPreview(doc: PTORequestDocument): void {
    this.documentPreviewService.open({
      id: doc.id,
      file_url: doc.attachment,
      original_name: doc.original_name,
      file_type: doc.file_type
    });
  }

  /**
   * Format date for display
   */
  formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  /**
   * Format date range
   */
  formatDateRange(startDate: string, endDate: string): string {
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (startDate === endDate) {
      return this.formatDate(startDate);
    }

    const startMonth = start.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    const endMonth = end.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    return `${startMonth} - ${endMonth}`;
  }

  /**
   * Get initials from name
   */
  getInitials(name: string): string {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  /**
   * Close dialog
   */
  close(): void {
    this.dialogRef.close({ refreshData: true });
  }
}
